import { memo, PropsWithChildren, useContext, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react'
import { useField, useFormState } from 'react-final-form'

import ConditionalField from 'components/ConditionalField'
import ErrorWrapper from 'components/ErrorWrapper'
import { PathFormComponent } from 'interfaces/PathFormComponents.interface'
import { FieldMetadataProvider } from 'context/fieldMetadata'
import { getNextUntouchedInput } from 'utils/input'
import { ProgressDispatchContext } from 'ProgressContext'

const FieldDataCollector = observer(
  ({ children, path, labelPath }: PropsWithChildren<PathFormComponent>) => {
    const {
      meta: { active = false }
    } = useField(path)
    const { touched = {} } = useFormState({ subscription: { touched: true } })
    const { setTouchedInput } = useContext(ProgressDispatchContext)

    const touchedInputs = useMemo(
      () =>
        Object.entries(touched).reduce((previous, [touchedPath, isTouched]) => {
          if (!isTouched) {
            return previous
          }

          return [...previous, touchedPath]
        }, [] as string[]),
      [touched]
    )

    useEffect(() => {
      if (!active) {
        return
      }

      setTouchedInput({
        last: path,
        next: getNextUntouchedInput(path, touchedInputs)?.name
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, path])

    return (
      <ConditionalField path={path}>
        <FieldMetadataProvider path={path} labelPath={labelPath}>
          <ErrorWrapper path={path}>{children}</ErrorWrapper>
        </FieldMetadataProvider>
      </ConditionalField>
    )
  }
)

export default memo(FieldDataCollector)
